// ── ErrorBoundary (2026-08-05, incidente real) ───────────────────────────────
// Antes: un error sin atrapar en cualquier componente (el bug real: un loop
// infinito de render en el registro del asistente) tiraba abajo TODO el árbol
// de React — pantalla en negro, sin ningún mensaje, sin forma de recuperarse
// salvo cerrar y volver a abrir la pestaña. Clase, no función — es la única
// forma que tiene React de implementar un error boundary (no hay hook
// equivalente a getDerivedStateFromError/componentDidCatch).
//
// Montado en main.tsx, envolviendo <App/> entero — así cubre cualquier
// crash, no solo los del shell autenticado (donde vivía el bug real).

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { EmptyState } from './ui/EmptyState.js';
import { Button } from './ui/Button.js';
import i18n from '../i18n.js';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- último recurso real: si esto no
    // llega a la consola, no queda ningún rastro del crash en absoluto.
    console.error('[ErrorBoundary] uncaught error', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--evo-bg)',
          padding: 'var(--evo-space-6)',
        }}>
          <EmptyState
            title={i18n.t('app.errorBoundary.title')}
            description={i18n.t('app.errorBoundary.description')}
            action={
              <Button variant="solid" tone="cyan" onClick={() => window.location.reload()}>
                {i18n.t('app.errorBoundary.reloadBtn')}
              </Button>
            }
          />
        </div>
      );
    }
    return this.props.children;
  }
}
